import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'

export const ResetPassword = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      if (response.status === 429) {
        setError('Troppe richieste. Riprova tra qualche minuto.')
        return
      }

      if (!response.ok) {
        // Anti-enumeration: anche su errore generico mostriamo conferma.
        // Mostriamo errore solo per status >= 500 (problema reale).
        if (response.status >= 500) {
          setError('Errore del server. Riprova più tardi.')
          return
        }
      }

      setSuccess(true)
    } catch (err) {
      setError('Errore di rete. Verifica la connessione e riprova.')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-10 w-10 text-primary" />
                <span className="text-2xl font-bold">iXen</span>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center space-y-4 text-center">
              <div className="rounded-full bg-green-500/10 p-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Controlla la tua email</h3>
                <p className="text-sm text-muted-foreground">
                  Se l'indirizzo <strong>{email}</strong> è registrato, riceverai a
                  breve un link per reimpostare la password.
                </p>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Il link è valido per 60 minuti. Controlla anche la cartella spam.
              </p>
            </div>

            <Link to="/login">
              <Button className="w-full" variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna al login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-10 w-10 text-primary" />
              <span className="text-2xl font-bold">iXen</span>
            </div>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Recupera password</CardTitle>
            <CardDescription>
              Inserisci la tua email: ti invieremo un link per impostare una nuova password.
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tuo.indirizzo@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Invio in corso...' : 'Invia link di reset'}
            </Button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                Torna al login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
